import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { effectiveInstallmentStatus } from "@/lib/installmentStatus";
import { edgeFunctionErrorMessage } from "@/lib/edgeFunctionError";
import { publicErrorMessage } from "@/lib/publicErrors";

// Payment functions answer with stable codes (already_paid, not_payable,
// mp_error…); translate them instead of the generic "non-2xx" message.
async function paymentError(error: unknown): Promise<Error> {
  return new Error(publicErrorMessage(await edgeFunctionErrorMessage(error, "mp_error")));
}

export function useCreateMpPreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { installment_id: string; max_installments?: number }) => {
      // back_urls come from PUBLIC_APP_URL on the server, never from the browser.
      const { data, error } = await supabase.functions.invoke("mp-create-preference", {
        body: args,
      });
      if (error) throw await paymentError(error);
      return data as { init_point: string; sandbox_init_point: string; preference_id: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["installments"] });
      qc.invalidateQueries({ queryKey: ["payment-plan"] });
      toast.success("Link de pagamento gerado");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useCreateMpPix() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      installment_id: string;
      payer_email?: string;
      payer_name?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke("mp-create-pix", { body: args });
      if (error) throw await paymentError(error);
      return data as { qr_code: string; qr_code_base64: string; payment_id: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["installments"] });
      qc.invalidateQueries({ queryKey: ["payment-plan"] });
      toast.success("PIX gerado");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export type InstallmentStatus = "pending" | "paid" | "overdue" | "refunded" | "canceled";
export type PaymentMode = "single" | "installments" | "deposit_balance";

export interface PaymentPlan {
  id: string;
  budget_id: string;
  mode: PaymentMode;
  installments_count: number;
  deposit_percent: number | null;
  total_amount: number;
  currency: string;
  notes: string | null;
  created_at: string;
}

export interface PaymentInstallment {
  id: string;
  plan_id: string;
  budget_id: string | null;
  client_id: string | null;
  sequence: number;
  label: string | null;
  amount: number;
  due_date: string | null;
  status: InstallmentStatus;
  paid_at: string | null;
  public_token: string | null;
  notes: string | null;
  mp_status: string | null;
  created_at: string;
}

export interface InstallmentWithRelations extends PaymentInstallment {
  budgets: {
    title: string;
    lead_id: string | null;
    client_projects: { title: string } | null;
  } | null;
  clients: { name: string; email: string } | null;
}

export function useInstallments(filters?: {
  status?: InstallmentStatus;
  client_id?: string;
  budget_id?: string;
}) {
  return useQuery({
    queryKey: ["installments", filters],
    queryFn: async () => {
      let q = supabase
        .from("payment_installments" as never)
        .select("*, budgets(title, lead_id, client_projects(title)), clients(name, email)")
        .order("due_date", { ascending: true, nullsFirst: false });
      if (filters?.status) q = q.eq("status", filters.status);
      if (filters?.client_id) q = q.eq("client_id", filters.client_id);
      if (filters?.budget_id) q = q.eq("budget_id", filters.budget_id);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as InstallmentWithRelations[];
    },
  });
}

export function usePaymentPlanByBudget(budgetId: string | undefined) {
  return useQuery({
    queryKey: ["payment-plan", budgetId],
    queryFn: async () => {
      if (!budgetId) return null;
      // A failed read must surface as an error, not as "no plan yet" — the
      // UI would offer to create a second plan for the same budget.
      const { data: planData, error: planError } = await supabase
        .from("payment_plans" as never)
        .select("*")
        .eq("budget_id", budgetId)
        .maybeSingle();
      if (planError) throw planError;
      if (!planData) return null;
      const plan = planData as PaymentPlan;
      const { data: installments, error: installmentsError } = await supabase
        .from("payment_installments" as never)
        .select("*")
        .eq("plan_id", plan.id)
        .order("sequence");
      if (installmentsError) throw installmentsError;
      return { plan, installments: (installments || []) as PaymentInstallment[] };
    },
    enabled: !!budgetId,
  });
}

export function useCreatePaymentPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      budget_id: string;
      client_id?: string | null;
      total_amount: number;
      mode: PaymentMode;
      installments_count: number;
      deposit_percent?: number;
      first_due_date?: string;
      interval_days?: number;
    }) => {
      // delete existing plan first (cascade removes installments)
      await supabase
        .from("payment_plans" as never)
        .delete()
        .eq("budget_id", args.budget_id);

      const { data: planData, error } = await supabase
        .from("payment_plans" as never)
        .insert({
          budget_id: args.budget_id,
          mode: args.mode,
          installments_count: args.installments_count,
          deposit_percent: args.deposit_percent ?? null,
          total_amount: args.total_amount,
        } as never)
        .select()
        .single();
      if (error) throw error;
      const plan = planData as PaymentPlan;

      type NewInstallmentRow = {
        plan_id: string;
        budget_id: string;
        client_id: string | null;
        sequence: number;
        label: string;
        amount: number;
        due_date: string;
      };
      const rows: NewInstallmentRow[] = [];
      const baseDate = args.first_due_date ? new Date(args.first_due_date) : new Date();
      const interval = args.interval_days ?? 30;

      if (args.mode === "single") {
        rows.push({
          plan_id: plan.id,
          budget_id: args.budget_id,
          client_id: args.client_id ?? null,
          sequence: 1,
          label: "Pagamento único",
          amount: args.total_amount,
          due_date: baseDate.toISOString().slice(0, 10),
        });
      } else if (args.mode === "deposit_balance") {
        const pct = (args.deposit_percent ?? 50) / 100;
        const deposit = +(args.total_amount * pct).toFixed(2);
        const balance = +(args.total_amount - deposit).toFixed(2);
        rows.push({
          plan_id: plan.id,
          budget_id: args.budget_id,
          client_id: args.client_id ?? null,
          sequence: 1,
          label: `Entrada (${Math.round(pct * 100)}%)`,
          amount: deposit,
          due_date: baseDate.toISOString().slice(0, 10),
        });
        const d2 = new Date(baseDate);
        d2.setDate(d2.getDate() + interval);
        rows.push({
          plan_id: plan.id,
          budget_id: args.budget_id,
          client_id: args.client_id ?? null,
          sequence: 2,
          label: "Saldo final",
          amount: balance,
          due_date: d2.toISOString().slice(0, 10),
        });
      } else {
        const n = Math.max(1, args.installments_count);
        const per = +(args.total_amount / n).toFixed(2);
        let acc = 0;
        for (let i = 1; i <= n; i++) {
          const amt = i === n ? +(args.total_amount - acc).toFixed(2) : per;
          acc += per;
          const d = new Date(baseDate);
          d.setDate(d.getDate() + interval * (i - 1));
          rows.push({
            plan_id: plan.id,
            budget_id: args.budget_id,
            client_id: args.client_id ?? null,
            sequence: i,
            label: `Parcela ${i}/${n}`,
            amount: amt,
            due_date: d.toISOString().slice(0, 10),
          });
        }
      }

      // remove auto-created single installment that the budget trigger may have created
      await supabase
        .from("payment_installments" as never)
        .delete()
        .eq("plan_id", plan.id);
      const { error: e2 } = await supabase
        .from("payment_installments" as never)
        .insert(rows as never);
      if (e2) throw e2;
      return plan;
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["payment-plan", v.budget_id] });
      qc.invalidateQueries({ queryKey: ["installments"] });
      toast.success("Plano de pagamento criado");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useMarkInstallment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: InstallmentStatus }) => {
      const { error } = await supabase
        .from("payment_installments" as never)
        .update({ status } as never)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["installments"] });
      qc.invalidateQueries({ queryKey: ["payment-plan"] });
      qc.invalidateQueries({ queryKey: ["finance-stats"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

// Hard delete de uma parcela avulsa — usado pra limpar cobrança de teste sem
// mexer no resto do plano. Também apaga a linha do ledger `payments` que o
// trigger record_payment_on_paid criou quando a parcela foi marcada como
// paga (o FK dela é SET NULL, não CASCADE — sem isso a linha ficaria órfã
// contando pra sempre nas estatísticas).
export function useDeleteInstallment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error: paymentsErr } = await supabase
        .from("payments" as never)
        .delete()
        .eq("installment_id", id);
      if (paymentsErr) throw paymentsErr;
      const { error } = await supabase
        .from("payment_installments" as never)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["installments"] });
      qc.invalidateQueries({ queryKey: ["payment-plan"] });
      qc.invalidateQueries({ queryKey: ["finance-stats"] });
      toast.success("Parcela excluída!");
    },
    onError: (e: Error) => toast.error("Erro ao excluir parcela: " + e.message),
  });
}

export function useFinanceStats() {
  return useQuery({
    queryKey: ["finance-stats"],
    queryFn: async () => {
      const [payments, installments, expenses, subscriptions] = await Promise.all([
        supabase.from("payments" as never).select("amount, net, received_at"),
        supabase.from("payment_installments" as never).select("amount, status, due_date"),
        supabase.from("expenses" as never).select("amount, recurring, incurred_on, category"),
        supabase
          .from("budget_subscriptions" as never)
          .select("amount")
          .eq("status", "authorized"),
      ]);
      // Any failed read would silently show R$ 0 on the dashboard.
      const failed = [payments, installments, expenses, subscriptions].find((r) => r.error);
      if (failed?.error) throw failed.error;
      type PaymentSummaryRow = { amount: number; net: number | null; received_at: string };
      type InstallmentSummaryRow = { amount: number; status: string; due_date: string | null };
      type ExpenseSummaryRow = {
        amount: number;
        recurring: string;
        incurred_on: string;
        category: string;
      };

      const pays = (payments.data || []) as PaymentSummaryRow[];
      const ins = (installments.data || []) as InstallmentSummaryRow[];
      const exps = (expenses.data || []) as ExpenseSummaryRow[];
      const mrr = ((subscriptions.data || []) as { amount: number }[]).reduce(
        (s, sub) => s + Number(sub.amount),
        0,
      );

      const now = new Date();
      const startMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startYear = new Date(now.getFullYear(), 0, 1);
      const start30 = new Date(now);
      start30.setDate(start30.getDate() - 30);
      const start90 = new Date(now);
      start90.setDate(start90.getDate() - 90);

      const sumIn = (from: Date) =>
        pays
          .filter((p) => new Date(p.received_at) >= from)
          .reduce((s, p) => s + Number(p.net ?? p.amount), 0);

      const receivable = ins
        .filter((i) => i.status === "pending" || i.status === "overdue")
        .reduce((s, i) => s + Number(i.amount), 0);
      const overdueCount = ins.filter((i) => effectiveInstallmentStatus(i) === "overdue").length;

      const expMonthly = exps.reduce((s, e) => {
        if (e.recurring === "monthly") return s + Number(e.amount);
        if (e.recurring === "yearly") return s + Number(e.amount) / 12;
        const d = new Date(e.incurred_on);
        return d >= startMonth ? s + Number(e.amount) : s;
      }, 0);

      // 12-month timeline
      const months: { month: string; revenue: number; expense: number }[] = [];
      for (let i = 11; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const next = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
        const label = d.toLocaleDateString("pt-BR", { month: "short" });
        const revenue = pays
          .filter((p) => {
            const x = new Date(p.received_at);
            return x >= d && x < next;
          })
          .reduce((s, p) => s + Number(p.net ?? p.amount), 0);
        const expense = exps
          .filter((e) => {
            const x = new Date(e.incurred_on);
            return x >= d && x < next;
          })
          .reduce((s, e) => s + Number(e.amount), 0);
        months.push({ month: label, revenue, expense });
      }

      return {
        revenueMonth: sumIn(startMonth),
        revenueYTD: sumIn(startYear),
        revenue30: sumIn(start30),
        revenue90: sumIn(start90),
        receivable,
        overdueCount,
        expenseMonthly: expMonthly,
        timeline: months,
        profitMonth: sumIn(startMonth) - expMonthly,
        mrr,
      };
    },
  });
}

export interface MpStatus {
  connected: boolean;
  account_id?: string | null;
  nickname?: string | null;
  site_id?: string | null;
  reason?: string;
}

/** Estado da conexão com o Mercado Pago (edge function mp-status, GET autenticado). */
export function useMpStatus() {
  return useQuery({
    queryKey: ["mp-status"],
    queryFn: async (): Promise<MpStatus> => {
      const { data, error } = await supabase.functions.invoke<MpStatus>("mp-status", {
        method: "GET",
      });
      if (error) throw new Error("Não foi possível checar a conexão com o Mercado Pago");
      return data as MpStatus;
    },
    staleTime: 5 * 60 * 1000,
  });
}
